import React, { useRef, useState } from 'react';
import { Box, DialogTitle, Slider, Tooltip } from '@mui/material';
import MinusIcon from '@mui/icons-material/RemoveCircleOutline';
import PlusIcon from '@mui/icons-material/AddCircleOutline';
import { useSnackbar } from 'material-ui-snackbar-provider';
import LinearProgress from '@mui/material/LinearProgress';
import AvatarEditor from 'react-avatar-editor';
import Resizer from 'react-image-file-resizer';
import DatePicker from 'react-datepicker';
import cs from 'classnames';
import dayjs from 'dayjs';

import { patchUploadOrgAvatarById, postOrganization } from '../../../api/organizationsApi';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import UploadFileIcon from '../../../assets/icons/upload-icon-file.svg';
import styles from './OrganizationsManagement.module.scss';
import { saveUser } from '../../../store/user/userSlice';
import CrossIcon from '../../../assets/icons/cross.svg';
import { useAppDispatch } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { RolesLowerCase } from '../../../enums';
import { getMyUserInfo } from '../../../api';
import OrgUserInput from './OrgUserInput';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';
import { useHotkeys } from 'react-hotkeys-hook';

type TCreateOrganizationModal = {
  open: boolean;
  switchModal: () => void;
  fetchOrganizations: () => void;
};

type TOrgFields = {
  allowedVideoQualities: boolean;
  name: string;
  location: string;
  users_limit: null | number;
  storage_limit: null | number;
  hours_limit: number;
  download_hours_limit: number;
  period_start: Date;
  period_end: null | string;
  type: 'child' | 'parent';
};

dayjs.locale('en-gb');

const CustomInputWithRequired = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>((props, ref) => (
  <input {...props} ref={ref} required style={{ ...props.style, border: '1px solid gray' }} />
));

const CreateOrganizationModal: React.FC<TCreateOrganizationModal> = ({ open, switchModal, fetchOrganizations }) => {
  const notification = useSnackbar();
  const [step, setStep] = useState(1);
  const toSecondStepButtonRef = useRef<HTMLDivElement>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLDivElement>(null);
  const avatarSubmitButtonRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(50);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgField, setOrgField] = useState<TOrgFields>({
    allowedVideoQualities: false,
    name: '',
    location: '',
    users_limit: null,
    storage_limit: null,
    hours_limit: 0,
    download_hours_limit: 0,
    period_start: new Date(),
    period_end: null,
    type: 'child',
  });

  const [adminFields, setAdminFields] = useState<{ email: string; username: string; id: number }[]>([
    { email: '', username: '', id: Date.now() },
  ]);

  const reset = () => {
    setAdminFields([{ email: '', username: '', id: Date.now() }]);
    setOrgField({
      allowedVideoQualities: false,
      name: '',
      location: '',
      users_limit: null,
      storage_limit: null,
      hours_limit: 0,
      download_hours_limit: 0,
      period_start: new Date(),
      period_end: null,
      type: 'child',
    });
  };

  const nextStep = (e?: any) => {
    if (e) {
      e.preventDefault();
    }
    setProgress(100);
    setStep(2);
  };

  const dispatch = useAppDispatch();
  const onSubmit = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    let proceed = true;
    if (
      !orgField.location ||
      !orgField.name ||
      !orgField.storage_limit ||
      !orgField.users_limit ||
      orgField.download_hours_limit === null ||
      orgField.download_hours_limit === undefined
    ) {
      notification.showMessage('Fill in all organisational fields.');
      proceed = false;
    }

    if (adminFields.length > 0 && adminFields.find((f) => !f.email.trim() || !f.username.trim())) {
      notification.showMessage('Fill in all user fields');
      proceed = false;
    }

    if (!proceed) {
      return;
    }
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const res = await postOrganization({
        ...orgField,
        // @ts-ignore
        users_limit: +orgField.users_limit,
        // @ts-ignore
        storage_limit: +orgField.storage_limit,
        admins: adminFields,
      });
      if (imgFileToSubmit && res?.data?.id) {
        const formData = new FormData();
        formData.append('file', imgFileToSubmit, 'compressed-image.png');
        await patchUploadOrgAvatarById(res.data.id, formData);
      }
      const userJson = await getMyUserInfo();
      dispatch(saveUser(userJson.data));
      setFinished(true);
      fetchOrganizations();
    } catch (err) {
      console.log(err, 'err');

      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_CREATED);
      }
    } finally {
      setLoading(false);
    }
  };

  const prevStep = () => {
    setStep(1);
  };

  const onClose = () => {
    switchModal();
    setImageUrl('');
    setIsFileUploaded(false);
    setShowUploadImg(false);
    setClick(false);
    setScale(1);
    reset();
    setProgress(50);
    setStep(1);
    setFinished(false);
    setImgFileToSubmit(null);
  };

  const removeUser = (id: number) => {
    const arr = [...adminFields].filter((o) => o.id !== id);
    setAdminFields(arr);
  };

  const addUser = () => {
    const newFields = [...adminFields];
    newFields.push({
      email: '',
      username: '',
      id: Date.now(),
    });
    setAdminFields(newFields);
  };

  const setUser = (data: { email: string; username: string; id: number }) => {
    const { id } = data;
    const userIndex = [...adminFields].findIndex((o) => o.id === id);
    if (userIndex !== -1) {
      const fields = [...adminFields];
      fields[userIndex] = {
        ...data,
      };
      setAdminFields(fields);
    }
  };

  const onOrgFieldsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgField({
      ...orgField,
      [e.target.name]: e.target.value,
    });
  };

  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showUploadImg, setShowUploadImg] = useState(false);
  const [imgUrl, setImageUrl] = useState('');
  const [click, setClick] = useState(false);
  const [scale, setScale] = useState<number>(1);
  const switchUploadImgModal = () => {
    setShowUploadImg(!showUploadImg);
  };
  const openPhotoUploader = () => {
    inputRef?.current?.click();
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<AvatarEditor>(null);
  const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    const reader = new FileReader();
    const file = e.dataTransfer.files[0];
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setImageUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(file);
  };
  const onDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const photofile = event.target?.files![0];

    const reader = new FileReader();

    reader.onloadend = () => {
      // the file's data URL is available in `reader.result`
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setImageUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(photofile);
  };
  const handleScale = (event: Event, value: number | number[]) => {
    setScale(value as number);
    setClick(true);
  };
  const cancel = () => {
    setImageUrl('');
    setIsFileUploaded(false);
    setImgFileToSubmit(null);
  };

  const onOrgExpirationDateChange = (date: Date | null) => {
    if (!date) {
      setOrgField({ ...orgField, period_end: null });
      return;
    }
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setOrgField({ ...orgField, period_end: formattedDate });
  };

  const [imgFileToSubmit, setImgFileToSubmit] = useState<Blob | null>(null);
  const submitImg = async () => {
    try {
      setLoading(true);
      if (editorRef.current) {
        const canvas = editorRef.current.getImage();
        const dataUrl = canvas.toDataURL();
        const blob = await fetch(dataUrl).then((res) => res.blob());
        setIsFileUploaded(!isFileUploaded);
        switchUploadImgModal();
        const compressedResult = await new Promise<Blob>((resolve) => {
          Resizer.imageFileResizer(
            blob,
            300,
            300,
            'JPEG',
            80,
            0,
            (uri) => {
              const resultBlob = fetch(uri as string).then((res) => res.blob());
              resolve(resultBlob);
            },
            'base64',
          );
        });
        setImageUrl(dataUrl);
        // PUT UPLOAD IMG HERE
        setImgFileToSubmit(compressedResult);
      }
    } catch (err) {
      notification.showMessage(NOTIFICATION.FILE_NOT_UPLOADED);
    } finally {
      setLoading(false);
    }
  };

  const switchAdditionalTranscoding = () => {
    setOrgField({
      ...orgField,
      allowedVideoQualities: !orgField.allowedVideoQualities,
    });
  };

  const switchParentOrganization = () => {
    setOrgField({
      ...orgField,
      type: orgField.type === 'child' ? 'parent' : 'child',
    });
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    if (showUploadImg && isFileUploaded) {
      avatarSubmitButtonRef?.current?.click();
      return;
    }

    switch (step) {
      case 1:
        toSecondStepButtonRef?.current?.click();
        break;
      case 2:
        submitButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  useHotkeys('Backspace', () => {
    if (!open || loading) return;

    if (step === 2) backButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <Modal open={showUploadImg} onClose={onClose} loading={loading}>
        {!isFileUploaded && (
          <div className={styles.uploadImgContainer}>
            <div className={styles.modalTitle}>
              Upload Avatar
              <img src={CrossIcon} alt="cross" onClick={switchUploadImgModal} />
            </div>
            <div onClick={openPhotoUploader} onDrop={onDrop} onDragOver={onDragOver} className={styles.photoUpload}>
              <div className={styles.innerUpload}>
                <input
                  max="5242880"
                  hidden
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  size={5e10}
                  accept="image/png, image/jpeg"
                />

                <img src={UploadFileIcon} alt="upload content " style={{ marginTop: '65px' }} />

                <div>
                  Upload designs by drag and drop or <span className={styles.text}> click to upload</span>.
                </div>
              </div>
            </div>
          </div>
        )}

        {isFileUploaded && (
          <div className={styles.uploadImgContainer}>
            <div className={styles.modalTitle} style={{ marginBottom: '20px' }}>
              Crop Avatar
              <img src={CrossIcon} alt="cross" onClick={switchModal} />
            </div>
            <div>
              <div>
                <AvatarEditor ref={editorRef} image={imgUrl} width={300} height={300} scale={click ? scale : 1} borderRadius={400} />

                <Box>
                  <div className={styles.slider}>
                    <MinusIcon fontSize="small" className={styles.minusIcon} />

                    <Slider
                      sx={{
                        '& .MuiSlider-thumb': {
                          color: '#39B3EA',
                        },
                        '& .MuiSlider-track': {
                          color: '#39b3ea',
                        },
                        '& .MuiSlider-rail': {
                          color: '#acc4e4',
                        },
                        '& .MuiSlider-active': {
                          color: 'green',
                        },
                      }}
                      defaultValue={1}
                      min={1}
                      max={20}
                      aria-label="Default"
                      valueLabelDisplay="auto"
                      onChange={handleScale}
                    />
                    <PlusIcon fontSize="small" className={styles.plusIcon} />
                  </div>
                </Box>
              </div>
              <div className={styles.uploadImgBtns}>
                <OutlinedButton kind="default" onClick={cancel}>
                  Cancel
                </OutlinedButton>
                <ContainedButton onClick={submitImg}>
                  <div ref={avatarSubmitButtonRef}>Save avatar</div>
                </ContainedButton>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {!finished ? (
        <>
          <div className={styles.modalTitle}>
            {step === 1 && (
              <DialogTitle fontSize={18} fontWeight={700}>
                Create Organisation
              </DialogTitle>
            )}
            {step === 2 && (
              <DialogTitle fontSize={18} fontWeight={700}>
                Add new {RolesLowerCase.ORGANIZATION_ADMIN.replace('Organization', 'Organisation')}
              </DialogTitle>
            )}
            <div className={styles.step} onClick={prevStep}>
              Step {step}/2
            </div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />

          {step === 1 && (
            <form onSubmit={nextStep} className={styles.form}>
              <div className={styles.avatarField}>
                <Avatar name={'avatar'} className={styles.userEditAvatar} avatar={imgUrl} />
                <div className={styles.uploadBtn1}>
                  <ContainedButton className={styles.contained} onClick={switchUploadImgModal}>
                    Upload Organisation Image
                  </ContainedButton>
                </div>
              </div>

              <Tooltip title={' Allow additional video qualities '} placement="left">
                <div className={styles.fieldNotify} onClick={switchAdditionalTranscoding}>
                  <div className={styles.notifyCheckBox}>
                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input type="checkbox" onChange={switchAdditionalTranscoding} checked={orgField.allowedVideoQualities} />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                      Additional Processing
                    </div>
                  </div>
                </div>
              </Tooltip>

              <Tooltip title={' Parent organisation '} placement="left">
                <div className={styles.fieldNotify} onClick={switchParentOrganization}>
                  <div className={styles.notifyCheckBox}>
                    <div className={styles.checkboxWrapper30}>
                      <span className={styles.checkbox}>
                        <input type="checkbox" onChange={switchParentOrganization} checked={orgField.type === 'parent'} />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={styles.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                      Parent organisation
                    </div>
                  </div>
                </div>
              </Tooltip>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>Name</span>
                <input value={orgField.name} required name="name" className={styles.field} onChange={onOrgFieldsChange} />
              </div>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>Location</span>
                <input value={orgField.location} required name="location" className={styles.field} onChange={onOrgFieldsChange} />
              </div>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>User Limit</span>
                <input
                  value={`${orgField.users_limit}`}
                  required
                  name="users_limit"
                  type="number"
                  className={styles.field}
                  onChange={onOrgFieldsChange}
                />
              </div>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>Storage Limit</span>
                <input
                  value={`${orgField.storage_limit}`}
                  required
                  type="number"
                  name="storage_limit"
                  className={styles.field}
                  onChange={onOrgFieldsChange}
                />
              </div>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>Hours Limit</span>
                <input
                  value={`${orgField.hours_limit}`}
                  required
                  type="number"
                  name="hours_limit"
                  className={styles.field}
                  onChange={onOrgFieldsChange}
                />
              </div>

              <div className={styles.fieldContainer}>
                <span className={styles.label}>Download Hours Limit</span>
                <input
                  value={`${orgField.download_hours_limit}`}
                  required
                  type="number"
                  name="download_hours_limit"
                  min="0"
                  className={styles.field}
                  onChange={onOrgFieldsChange}
                />
              </div>

              <div className={styles.fieldContainer}>
                <span className={cs(styles.label, styles.endDateField)}>Hours limit period end</span>
                <DatePicker
                  selected={
                    orgField.period_end
                      ? dayjs(orgField.period_end, 'DD-MM-YYYY').isValid()
                        ? dayjs(orgField.period_end, 'DD-MM-YYYY').toDate()
                        : null
                      : null
                  }
                  onChange={onOrgExpirationDateChange}
                  dateFormat="dd-MM-yyyy"
                  locale="en-GB"
                  placeholderText="DD-MM-YYYY"
                  className={styles.field}
                  minDate={dayjs().add(1, 'day').toDate()}
                  customInput={<CustomInputWithRequired />}
                />
              </div>

              <div className={styles.actionBtns}>
                <ContainedButton disabled={loading} type="submit">
                  <div ref={toSecondStepButtonRef}>Next</div>
                </ContainedButton>

                <OutlinedButton type="button" kind="default" onClick={onClose}>
                  Cancel
                </OutlinedButton>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={onSubmit} className={styles.form}>
              {adminFields.map((field, index) => (
                <OrgUserInput setUser={setUser} key={field.id} index={index} removeUser={removeUser} data={field} />
              ))}
              <div className={styles.actionBtns}>
                <button type="button" className={styles.addMore} onClick={addUser}>
                  + Add one more?
                </button>
                <ContainedButton disabled={adminFields.length === 0 || loading} type="submit">
                  <div ref={submitButtonRef}>Create</div>
                </ContainedButton>

                <OutlinedButton disabled={loading} type="button" kind="default" onClick={prevStep}>
                  <div ref={backButtonRef}>Back</div>
                </OutlinedButton>
              </div>
            </form>
          )}
        </>
      ) : (
        <div className={styles.successModal}>
          <div className={styles.title}>You have successfully created new organisation 🎉</div>
          <ContainedButton onClick={onClose}>Close</ContainedButton>
        </div>
      )}
    </Modal>
  );
};

export default CreateOrganizationModal;
